import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import type { OpenRole, OpenRoleFilters } from '@/types/openRole'

export const useRolesStore = defineStore('roles', () => {
  // State
  const roles = ref<OpenRole[]>([])
  const selectedRole = ref<OpenRole | null>(null)
  const isLoadingSelected = ref(false)
  const isLoadingList = ref(false)
  const error = ref<string | null>(null)
  const totalCount = ref(0)
  const realTotalCount = ref<number | null>(null)
  const currentPage = ref(1)
  const pageSize = ref(20)
  const filters = ref<Partial<OpenRoleFilters>>({})


  // Computed
  const hasNextPage = computed(() => currentPage.value * pageSize.value < totalCount.value)
  const hasPrevPage = computed(() => currentPage.value > 1)
  const totalPages = computed(() => Math.ceil(totalCount.value / pageSize.value))

  // Actions
  const setFilters = (newFilters: Partial<OpenRoleFilters>) => {
    filters.value = { ...newFilters }
  }

  const setPage = (page: number) => {
    currentPage.value = page
  }

  const setPageSize = (size: number) => {
    pageSize.value = size
  }

  const fetchRoles = async () => {
    return fetchRolesWithParams({
      page: currentPage.value,
      filters: filters.value
    })
  }

  // eslint-disable-next-line complexity
  const fetchRolesWithParams = async (params: {
    page?: number
    search?: string
    filters?: Partial<OpenRoleFilters>
  } = {}) => {
    isLoadingList.value = true
    error.value = null

    try {
      const queryParams = new URLSearchParams()
      
      // Handle pagination
      const page = params.page || currentPage.value
      queryParams.append('page', page.toString())
      queryParams.append('page_size', pageSize.value.toString())

      // Handle search
      if (params.search) {
        queryParams.append('search', params.search)
      }

      // Handle filters
      const currentFilters = params.filters || filters.value
      
      if (currentFilters.status_open !== undefined) {
        queryParams.append('status_open', currentFilters.status_open.toString())
      }

      if (currentFilters.is_scraped) {
        queryParams.append('is_scraped', String(currentFilters.is_scraped))
      }

      if (currentFilters.time_posted) {
        queryParams.append('time_posted', currentFilters.time_posted)
      }

      if (currentFilters.seniority_level) {
        queryParams.append('seniority_level', currentFilters.seniority_level)
      }

      // Handle multiple country IDs
      if (currentFilters.country_ids && Array.isArray(currentFilters.country_ids)) {
        currentFilters.country_ids.forEach(id => {
          queryParams.append('country_ids', id.toString())
        })
      }

      // Handle multiple location IDs
      if (currentFilters.location_ids && Array.isArray(currentFilters.location_ids)) {
        currentFilters.location_ids.forEach(id => {
          queryParams.append('location_ids', id.toString())
        })
      }

      // Handle multiple employer IDs
      if (currentFilters.employer_ids && Array.isArray(currentFilters.employer_ids)) {
        currentFilters.employer_ids.forEach(id => {
          queryParams.append('employer_ids', id.toString())
        })
      }

      const response = await api.get(`${API_ROUTES.GET_ROLES}?${queryParams.toString()}`)
      
      roles.value = response.data.results
      totalCount.value = response.data.count
      realTotalCount.value = response.data.real_total ?? null
      currentPage.value = page

      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.detail || err.response?.data?.message || 'Failed to fetch roles'
      throw err
    } finally {
      isLoadingList.value = false
    }
  }

  ///////////////////////////////////////////////

  const fetchRoleById = async (id: string | number) => {
    const normalizedId = typeof id === 'string' ? Number(id) : id

    // If we already have the role with the same id, return it instead of refetching
    if (selectedRole.value && selectedRole.value.id === normalizedId) {
      return selectedRole.value
    }

    isLoadingSelected.value = true
    error.value = null

    try {
      const response = await api.get(API_ROUTES.GET_ROLE_BY_ID(normalizedId.toString()))
      selectedRole.value = response.data
      return response.data
    } catch (err: any) {
      error.value =
        err.response?.data?.detail || err.response?.data?.message || 'Failed to fetch role'
      throw err
    } finally {
      isLoadingSelected.value = false
    }
  }

  const updateRole = async (id: string, data: Partial<OpenRole>) => {
    isLoadingSelected.value = true
    error.value = null

    try {
      const response = await api.patch(API_ROUTES.UPDATE_ROLE(id), data)
      if (selectedRole.value?.id === Number(id)) {
        selectedRole.value = response.data
      }
      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update role'
      throw err
    } finally {
      isLoadingSelected.value = false
    }
  }

  const createRole = async (data: Partial<OpenRole>) => {
    isLoadingList.value = true
    error.value = null

    try {
      const response = await api.post(API_ROUTES.CREATE_ROLE, data)
      return response.data
    } catch (err: any) {
      console.error('Create role error:', err.response?.data || err)
      // Handle validation errors
      if (err.response?.status === 400 && err.response?.data) {
        // Pass through the validation errors directly
        throw err.response.data
      }
      error.value =
        err.response?.data?.detail || err.response?.data?.message || 'Failed to create role'
      throw err
    } finally {
      isLoadingList.value = false
    }
  }

  const deleteRole = async (id: string) => {
    isLoadingList.value = true
    isLoadingSelected.value = true
    error.value = null

    try {
      await api.delete(API_ROUTES.DELETE_ROLE(id))
      // Remove the deleted role from the local state
      roles.value = roles.value.filter((role) => role.id !== Number(id))
      if (selectedRole.value?.id === Number(id)) {
        selectedRole.value = null
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to delete role'
      throw err
    } finally {
      isLoadingList.value = false
      isLoadingSelected.value = false
    }
  }

 

  return {
    // State
    roles,
    selectedRole,
    isLoadingList,
    isLoadingSelected,
    error,
    totalCount,
    realTotalCount,
    currentPage,
    pageSize,

    // Computed
    hasNextPage,
    hasPrevPage,
    totalPages,

    // Actions
    fetchRoles,
    fetchRolesWithParams,
    setPage,
    setPageSize,
    fetchRoleById,
    updateRole,
    createRole,
    deleteRole,
    filters,
    setFilters
  }
})
